import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, ChevronRight, Award, Truck, MapPin, Check, Loader2, ArrowLeft, Clock, Edit2, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format, addDays, isWeekend, startOfToday } from 'date-fns';
import { canStillOrderToday, isInGraceWindow, graceMinutesLeft } from '@/lib/cutoff';
import DeliveryAddressSelector from './DeliveryAddressSelector';
import CartItem from './CartItem';
import { API_URL } from '@/config/api';

export default function CartDrawer() {
  const { 
    items, isCartOpen, closeCart, total, itemCount, 
    updateQuantity, removeItem, clearCart, getItemsByDeliveryDate, getDeliveryDates,
    updateItemDeliveryDate
  } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const [showDeliverySelector, setShowDeliverySelector] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [platformFees, setPlatformFees] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [editingDate, setEditingDate] = useState(null);

  // Available weekday dates for delivery (next 7) — today only before the 9AM cut-off (+10 min grace)
  const getAvailableDates = useCallback(() => {
    const dates = [];
    let current = startOfToday();
    if (!canStillOrderToday()) current = addDays(current, 1);
    let count = 0;
    while (count < 7) {
      if (!isWeekend(current)) { dates.push(current); count++; }
      current = addDays(current, 1);
    }
    return dates;
  }, []);

  useEffect(() => {
    if (isCartOpen && items.length > 0) {
      fetchPlatformFees();
      if (user && token) fetchLoyaltyStatus();
    }
  }, [isCartOpen, user, token, items.length]);

  const fetchPlatformFees = async () => {
    try {
      const r = await fetch(`${API_URL}/api/platform-fees`);
      if (r.ok) setPlatformFees(await r.json());
    } catch {}
  };

  const fetchLoyaltyStatus = async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/loyalty/status`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) setLoyaltyStatus(await r.json());
    } catch {}
  };

  const loyaltyDiscount = (() => {
    if (!loyaltyStatus?.eligible) return 0;
    const eligible = items.filter(i => i.menu_type === 'daily_meals' || !i.menu_type);
    return Math.round(eligible.reduce((s, i) => s + i.price * i.quantity, 0) * 0.10 * 100) / 100;
  })();

  const serviceFee = (() => {
    if (!platformFees) return 0;
    if (platformFees.service_fee_fixed > 0) return platformFees.service_fee_fixed;
    return total * (platformFees.service_fee_percent / 100);
  })();

  const deliveryFee = (() => {
    if (!deliveryAddress) return 0;
    if (platformFees && total >= platformFees.free_delivery_threshold) return 0;
    return deliveryAddress.delivery_fee || 0;
  })();

  const finalTotal = total - loyaltyDiscount + deliveryFee + serviceFee;

  const formatDate = (d) => {
    if (!d || d === 'unspecified') return 'Date not set';
    try { return format(new Date(d), 'EEE, MMM d'); } catch { return d; }
  };

  const handleCheckout = async () => {
    if (checkingOut) return;
    if (!user) { closeCart(); navigate('/auth'); return; }
    if (items.some(i => !i.delivery_date)) { toast.error('Some items are missing delivery dates'); return; }
    if (!deliveryAddress) { toast.error('Please select a delivery address'); setShowDeliverySelector(true); return; }

    setCheckingOut(true);
    try {
      const deliveryInfo = {
        address_type: deliveryAddress.type, address_id: deliveryAddress.address_id,
        address_name: deliveryAddress.name, full_address: deliveryAddress.full_address,
        delivery_fee: deliveryFee, service_fee: serviceFee, distance_km: deliveryAddress.distance_km || 0
      };
      const deliveryDates = getDeliveryDates();
      const isMultiDay = deliveryDates.length > 1;
      const endpoint = isMultiDay ? `${API_URL}/api/orders/multi-day` : `${API_URL}/api/orders`;
      const body = isMultiDay
        ? { items: items.map(i => ({ item_id: i.item_id, name: i.name, quantity: i.quantity, price: i.price, delivery_date: i.delivery_date, menu_type: i.menu_type, selected_sides: i.selected_sides || [], sides_total: i.sides_total || 0 })), delivery_info: deliveryInfo, loyalty_discount: loyaltyDiscount }
        : { items: items.map(i => ({ item_id: i.item_id, name: i.name, quantity: i.quantity, price: i.price, delivery_date: i.delivery_date, menu_type: i.menu_type, selected_sides: i.selected_sides || [], sides_total: i.sides_total || 0 })), delivery_date: deliveryDates[0] || items[0]?.delivery_date, delivery_info: deliveryInfo, loyalty_discount: loyaltyDiscount };

      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create order');
      }
      const result = await r.json();
      closeCart();
      if (isMultiDay) navigate(`/checkout/${result.order_ids[0]}?parent=${result.parent_order_id}`);
      else navigate(`/checkout/${result.order_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCheckingOut(false);
    }
  };

  const itemsByDate = getItemsByDeliveryDate();
  const sortedDates = Object.keys(itemsByDate).sort();
  const availableDates = getAvailableDates();
  const orderDrawerSameDay = canStillOrderToday();
  const orderDrawerGrace = isInGraceWindow();

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm"
          />

          {/* Cart Panel - Uber Eats style */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            className="fixed top-0 right-0 bottom-0 z-[60] bg-white flex flex-col shadow-2xl w-full sm:w-[420px] md:w-[440px]"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              <button
                onClick={closeCart}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-700" />
              </button>
              <div className="flex-1">
                <h2 className="font-heading text-lg font-bold text-gray-900">Your Cart</h2>
                {itemCount > 0 && (
                  <p className="text-xs text-gray-500">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
                )}
              </div>
              {items.length > 0 && (
                <button
                  onClick={() => { if (confirm('Clear all items?')) clearCart(); }}
                  className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-20 px-8 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-5">
                    <ShoppingBag className="h-9 w-9 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">Your cart is empty</h3>
                  <p className="text-gray-500 text-sm mb-6">Add items from the menu to get started</p>
                  <button
                    onClick={() => { closeCart(); navigate('/menu'); }}
                    className="px-8 py-3 bg-primary text-white rounded-2xl font-semibold text-sm"
                  >
                    Browse Menu
                  </button>
                  <button
                    onClick={() => { closeCart(); navigate('/profile?action=buy-voucher'); }}
                    className="mt-3 flex items-center gap-2 text-sm text-primary font-medium"
                    data-testid="cart-empty-gift-voucher"
                  >
                    <Gift className="h-4 w-4" /> Or gift a voucher
                  </button>
                </div>
              ) : (
                <div>
                  {/* ── Items grouped by delivery date ── */}
                  {sortedDates.map((date) => (
                    <div key={date}>
                      {/* Date group header */}
                      <div className="flex items-center justify-between px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5 text-primary" />
                          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            Delivery: {formatDate(date)}
                          </span>
                          {date === format(new Date(), 'yyyy-MM-dd') && !canStillOrderToday() && (
                            <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5" data-testid={`past-cutoff-${date}`}>
                              past cut-off — pick a new day
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setEditingDate(editingDate === date ? null : date)}
                          className="flex items-center gap-1 text-xs text-primary font-medium"
                          data-testid={`edit-date-${date}`}
                        >
                          <Edit2 className="h-3 w-3" />
                          Change
                        </button>
                      </div>

                      {/* Date picker (inline) */}
                      <AnimatePresence>
                        {editingDate === date && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden border-b border-gray-100 bg-white"
                          >
                            <div className="px-5 py-3">
                              {orderDrawerGrace && (
                                <p className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5 mb-2" data-testid="grace-window-note">
                                  The 9:00 AM cut-off has passed — {graceMinutesLeft()} min of grace left to keep today as your delivery day.
                                </p>
                              )}
                              {!orderDrawerSameDay && (
                                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2" data-testid="cutoff-passed-note">
                                  Today's 9:00 AM cut-off has passed — orders can now only be delivered from the next delivery day.
                                </p>
                              )}
                              <div className="flex flex-wrap gap-2">
                              {availableDates.map(newDate => {
                                const ds = format(newDate, 'yyyy-MM-dd');
                                return (
                                  <button
                                    key={ds}
                                    onClick={() => {
                                      if (ds !== date) {
                                        itemsByDate[date].forEach(item => updateItemDeliveryDate?.(item.item_id, item.delivery_date, ds));
                                      }
                                      setEditingDate(null);
                                    }}
                                    className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${
                                      ds === date ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                  >
                                    {format(newDate, 'EEE d MMM')}
                                  </button>
                                );
                              })}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Items — use CartItem sub-component */}
                      {itemsByDate[date].map((item) => (
                        <CartItem
                          key={item.cart_key || `${item.item_id}_${item.delivery_date}`}
                          item={item}
                          onUpdateQty={(i, qty) => updateQuantity(i.item_id, qty, i.delivery_date, i.cart_key)}
                          onRemove={(i) => removeItem(i.item_id, i.delivery_date, i.cart_key)}
                        />
                      ))}
                    </div>
                  ))}

                  {/* ── Add more items ── */}
                  <div className="px-5 py-3 border-b border-gray-100">
                    <button
                      onClick={() => { closeCart(); navigate('/menu'); }}
                      className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-primary hover:text-primary font-medium transition-colors"
                    >
                      + Add more items
                    </button>
                  </div>

                  {/* ── Delivery address ── */}
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Delivery</p>
                    <button
                      onClick={() => setShowDeliverySelector(!showDeliverySelector)}
                      className={`w-full p-3.5 rounded-xl flex items-center gap-3 transition-all border ${
                        deliveryAddress
                          ? 'bg-green-50 border-green-200'
                          : 'bg-amber-50 border-amber-200'
                      }`}
                      data-testid="delivery-address-toggle"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        deliveryAddress ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {deliveryAddress ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Truck className="h-4 w-4 text-amber-600" />
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className={`text-sm font-semibold ${deliveryAddress ? 'text-green-800' : 'text-amber-800'}`}>
                          {deliveryAddress ? deliveryAddress.name : 'Select delivery address'}
                        </p>
                        {deliveryAddress && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">{deliveryAddress.full_address}</p>
                        )}
                      </div>
                      <ChevronRight className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${showDeliverySelector ? 'rotate-90' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {showDeliverySelector && user && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-3">
                            <DeliveryAddressSelector
                              selectedAddress={deliveryAddress?.address_id}
                              onAddressChange={(addr) => { setDeliveryAddress(addr); setShowDeliverySelector(false); }}
                              subtotal={total}
                              token={token}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* ── Loyalty banner ── */}
                  {user && loyaltyStatus && (
                    <div className={`mx-5 my-3 p-3.5 rounded-xl flex items-start gap-3 ${
                      loyaltyStatus.eligible
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`} data-testid="loyalty-banner">
                      <Award className={`h-5 w-5 flex-shrink-0 mt-0.5 ${loyaltyStatus.eligible ? 'text-amber-500' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        {loyaltyStatus.eligible ? (
                          <>
                            <p className="text-sm font-bold text-amber-800">10% Loyalty Discount!</p>
                            <p className="text-xs text-amber-600 mt-0.5">{loyaltyStatus.message}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs font-semibold text-gray-700">
                              {loyaltyStatus.meals_this_month}/10 meals — {loyaltyStatus.meals_needed} more for 10% off
                            </p>
                            <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary"
                                style={{ width: `${Math.min((loyaltyStatus.meals_this_month / 10) * 100, 100)}%` }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Price breakdown ── */}
                  <div className="px-5 py-4 space-y-2.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Price breakdown</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium text-gray-900">R{total.toFixed(2)}</span>
                    </div>
                    {loyaltyDiscount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600 flex items-center gap-1.5">
                          <Award className="h-3.5 w-3.5" /> Loyalty discount (10%)
                        </span>
                        <span className="font-medium text-green-600">-R{loyaltyDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {deliveryAddress && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 flex items-center gap-1.5">
                          <Truck className="h-3.5 w-3.5" />
                          Delivery{deliveryAddress.type === 'pickup' ? ' (Pickup)' : ''}
                        </span>
                        <span className={`font-medium ${deliveryFee === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {deliveryFee === 0 ? 'FREE' : `R${deliveryFee.toFixed(2)}`}
                        </span>
                      </div>
                    )}
                    {serviceFee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Service fee</span>
                        <span className="font-medium text-gray-900">R{serviceFee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* Extra bottom padding for footer */}
                  <div className="h-2" />
                </div>
              )}
            </div>

            {/* ── Sticky checkout footer ── */}
            {items.length > 0 && (
              <div className="flex-shrink-0 border-t border-gray-100 bg-white px-5 pt-4 pb-6">
                {/* Total */}
                <div className="flex items-center justify-between mb-4">
                  <span className="font-bold text-gray-900 text-base">Total</span>
                  <div className="text-right">
                    {(loyaltyDiscount > 0 || serviceFee > 0 || deliveryFee > 0) && (
                      <p className="text-xs text-gray-400 line-through">R{total.toFixed(2)}</p>
                    )}
                    <p className="text-2xl font-black text-gray-900">R{finalTotal.toFixed(2)}</p>
                  </div>
                </div>

                {/* Warning if no address */}
                {user && !deliveryAddress && (
                  <div className="mb-3 flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                    <MapPin className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <p className="text-xs text-amber-700 font-medium">Select a delivery address to continue</p>
                  </div>
                )}

                {/* Checkout button */}
                <button
                  onClick={handleCheckout}
                  disabled={(user && !deliveryAddress) || checkingOut}
                  className="w-full h-14 bg-primary text-white rounded-2xl font-bold text-base flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/30"
                  data-testid="checkout-btn"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : !user ? (
                    'Sign in to checkout'
                  ) : !deliveryAddress ? (
                    'Select delivery address'
                  ) : (
                    <>
                      Place Order — R{finalTotal.toFixed(2)}
                      <ChevronRight className="h-5 w-5" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 mt-2">
                  Secure payment via Paystack
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
